import apiClient from '@/lib/api/client'
import type {
  StoreSearchResult,
  TicketAttachmentMetadata,
  TicketFormOptions,
  TicketRequest,
  TicketRequestSourceEmail,
  WebTicketRequestBody,
} from '@/types/ticketRequest'
import type { CreateTicketPayload } from '@/components/client/CreateTicketModal'

function unwrapData<T>(payload: unknown): T {
  if (payload != null && typeof payload === 'object' && 'data' in payload) {
    return (payload as { data: T }).data
  }
  return payload as T
}

/** Format split date + time to backend "Y-m-d H:i:s", or null when incomplete */
export function toBackendDateTimeFromParts(date: string, time: string): string | null {
  if (!date?.trim() || !time?.trim()) return null
  return `${date.trim()} ${time.trim()}:00`
}

/** Format backend "Y-m-d H:i:s" or ISO to datetime-local value */
export function backendDateTimeToLocal(value: string | null | undefined): string {
  if (!value) return ''
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return ''
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  const h = String(d.getHours()).padStart(2, '0')
  const min = String(d.getMinutes()).padStart(2, '0')
  return `${y}-${m}-${day}T${h}:${min}`
}

/** Map Laravel validation errors to form field keys */
export function parseApiValidationErrors(err: unknown): Record<string, string> {
  const ax = err as { response?: { data?: { errors?: Record<string, string[]> } } }
  const errs = ax.response?.data?.errors
  if (!errs) return {}

  const fieldMap: Record<string, string> = {
    service_type_id: 'serviceParent',
    description: 'description',
    contact_number: 'contactNumber',
    contact_name: 'contactPerson',
    contact_email: 'email',
    requested_quantity: 'devicesAffected',
    preferred_schedule_at: 'preferredScheduleAt',
    preferred_schedule_until: 'preferredScheduleUntil',
    preferred_contact_at: 'preferredContactAt',
    store_code: 'storeCode',
    site_address: 'siteAddress',
    error_message: 'errorMessage',
  }

  const out: Record<string, string> = {}
  for (const [key, messages] of Object.entries(errs)) {
    const formKey = fieldMap[key] ?? key
    if (messages?.length) out[formKey] = messages.join(' ')
  }
  return out
}

/** Format datetime-local value to backend "Y-m-d H:i:s" */
export function toBackendDateTime(value: string): string | null {
  if (!value || value.length < 16) return null
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return null
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  const h = String(d.getHours()).padStart(2, '0')
  const min = String(d.getMinutes()).padStart(2, '0')
  const s = String(d.getSeconds()).padStart(2, '0')
  return `${y}-${m}-${day} ${h}:${min}:${s}`
}

export async function getTicketRequest(id: number): Promise<TicketRequest> {
  const res = await apiClient.get(`/support/my-request/${id}`)
  return unwrapData<TicketRequest>(res.data)
}

export async function getTicketRequestSourceEmail(
  ticketRequestId: number
): Promise<TicketRequestSourceEmail> {
  const res = await apiClient.get<{ data: TicketRequestSourceEmail }>(
    `/support/my-request/${ticketRequestId}/source-email`,
    { timeout: 45000 }
  )
  return unwrapData<TicketRequestSourceEmail>(res.data)
}

export async function searchStores(query: string): Promise<StoreSearchResult[]> {
  const q = query.trim()
  if (q.length < 3) return []
  const res = await apiClient.get('/options/stores/search', { params: { search: q } })
  const raw = res.data
  if (Array.isArray(raw)) return raw as StoreSearchResult[]
  if (raw && typeof raw === 'object' && Array.isArray((raw as { data?: unknown }).data)) {
    return (raw as { data: StoreSearchResult[] }).data
  }
  return []
}

export async function loadTicketFormOptions(): Promise<TicketFormOptions> {
  const typesRes = await apiClient.get('/options/service-types')

  const allTypes = Array.isArray(typesRes.data) ? typesRes.data : []
  const serviceParents = allTypes
    .filter((s: { parent_id?: number | null }) => !s.parent_id)
    .map((s: { id: number; name?: string }) => ({
      label: s.name || String(s.id),
      value: String(s.id),
    }))
  const serviceChildren = allTypes
    .filter((s: { parent_id?: number | null }) => s.parent_id)
    .map((s: { id: number; name?: string; parent_id?: number }) => ({
      label: s.name || String(s.id),
      value: String(s.id),
      parent_id: String(s.parent_id),
    }))

  return {
    serviceParents,
    serviceChildren,
    categories: [],
  }
}

function optionalString(value: string | undefined | null): string | null {
  const v = (value ?? '').trim()
  return v || null
}

export function buildWebTicketRequestBody(
  payload: CreateTicketPayload,
  attachmentMetadata: TicketAttachmentMetadata[] = []
): WebTicketRequestBody {
  return {
    service_type_id: Number(payload.service),
    category_ids: [],
    description: payload.description,
    requested_quantity: Number(payload.devicesAffected || 1),
    preferred_schedule_at: toBackendDateTime(payload.preferredScheduleAt),
    preferred_schedule_until: toBackendDateTime(payload.preferredScheduleUntil),
    preferred_contact_at: toBackendDateTime(payload.preferredContactAt),
    attachment_metadata: attachmentMetadata,
    contact_number: payload.contactNumber,
    contact_name: payload.contactPerson,
    contact_email: payload.email,
    request_source: 'client_portal',
    request_channel: 'portal',
    submit_request: Boolean(payload.submitRequest),
    store_id: payload.storeId ? Number(payload.storeId) : null,
    store_code: optionalString(payload.storeCode),
    site_address: optionalString(payload.siteAddress),
    region_id: payload.regionId ? Number(payload.regionId) : null,
    province_id: payload.provinceId ? Number(payload.provinceId) : null,
    town_id: payload.townId ? Number(payload.townId) : null,
    needs_human_review: Boolean(payload.needsHumanReview),
    error_message: optionalString(payload.errorMessage),
    work_impact: optionalString(payload.workImpact),
    others_affected: optionalString(payload.othersAffected),
    user_scope: optionalString(payload.userScope),
    app_scope: optionalString(payload.appScope),
    access_type: optionalString(payload.accessType),
    work_permit_needed: optionalString(payload.workPermitNeeded),
    permit_available: optionalString(payload.permitAvailable),
    permit_duration: optionalString(payload.permitDuration),
    parts_approved: optionalString(payload.partsApproved),
    close_on_fix: optionalString(payload.closeOnFix),
    contact_availability_text: optionalString(payload.contactAvailabilityText),
  }
}

export async function uploadTicketAttachments(files: File[]): Promise<TicketAttachmentMetadata[]> {
  if (!files.length) return []

  const uploadForm = new FormData()
  files.forEach((f) => uploadForm.append('files[]', f))

  const uploadRes = await apiClient.post('/files/upload-multiple', uploadForm, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })

  if (!uploadRes?.data?.success || !uploadRes?.data?.data) return []

  const uploadedFiles = Array.isArray(uploadRes.data.data)
    ? uploadRes.data.data
    : [uploadRes.data.data]

  return uploadedFiles.map((r: Record<string, string>) => ({
    file_name: r.file_name || r.filename || '',
    path: r.file_url || r.path || '',
  }))
}

export async function createWebTicketRequest(body: WebTicketRequestBody): Promise<TicketRequest> {
  const res = await apiClient.post('/support/my-request/web', body)
  return unwrapData<TicketRequest>(res.data)
}

export async function updateWebTicketRequest(
  id: number,
  body: WebTicketRequestBody
): Promise<TicketRequest> {
  const res = await apiClient.put(`/support/my-request/web/${id}`, body)
  return unwrapData<TicketRequest>(res.data)
}

export async function submitTicketRequestForm(
  payload: CreateTicketPayload,
  editingId?: number | null
): Promise<TicketRequest> {
  let attachmentMetadata: TicketAttachmentMetadata[] = []

  if (Array.isArray(payload.existingAttachments) && payload.existingAttachments.length > 0) {
    attachmentMetadata = payload.existingAttachments.map((att) => ({
      file_name: att.file_name,
      path: att.file_url || att.path,
    }))
  }

  if (Array.isArray(payload.attachments) && payload.attachments.length > 0) {
    const uploaded = await uploadTicketAttachments(payload.attachments)
    attachmentMetadata = [...attachmentMetadata, ...uploaded]
  }

  const body = buildWebTicketRequestBody(payload, attachmentMetadata)

  if (editingId != null) {
    return updateWebTicketRequest(editingId, body)
  }
  return createWebTicketRequest(body)
}
